/**
 * Cart adapter
 * Converts Storefront API Cart to Analyzify format
 */

import type { StorefrontCart, StorefrontCartLine } from '../types/storefront';
import type { AnalyzifyCart, AnalyzifyCartItem } from '../types/products';
import { extractNumericId, extractCartToken } from './ids';

/**
 * Convert money string to cents
 * Defensively handles null/undefined/NaN amounts
 */
function moneyToCents(amount: string | null | undefined): number {
  if (!amount) return 0;
  const parsed = parseFloat(amount);
  return isNaN(parsed) ? 0 : Math.round(parsed * 100);
}

/**
 * Create empty cart item for null inputs
 */
function createEmptyCartItem(): AnalyzifyCartItem {
  return {
    id: 0,
    key: '',
    product_id: 0,
    variant_id: 0,
    product_title: '',
    variant_title: '',
    title: '', // Combined title for FB CDN script
    price: 0,
    quantity: 0,
    sku: '',
    vendor: '',
    product_type: '',
    type: '', // Alias for FB CDN script
    handle: '',
    url: '',
    image: '',
    properties: {},
  };
}

/**
 * Adapt a cart line to Analyzify cart item format
 * Defensively handles null/undefined line data
 */
export function adaptCartItem(line: StorefrontCartLine | null | undefined): AnalyzifyCartItem {
  // Return safe default for null/undefined line
  if (!line) {
    return createEmptyCartItem();
  }

  const merchandise = line.merchandise;
  if (!merchandise) {
    return createEmptyCartItem();
  }

  const product = merchandise.product || {};
  const handle = product.handle || '';
  const attributes = Array.isArray(line.attributes) ? line.attributes : [];

  const productTitle = product.title || '';
  const variantTitle = merchandise.title || '';
  const productType = product.productType || '';

  // Create combined title: "Product Title - Variant Title" or just "Product Title" if variant is Default
  const title =
    variantTitle && variantTitle !== 'Default Title'
      ? `${productTitle} - ${variantTitle}`
      : productTitle;

  return {
    // Shopify Ajax Cart contract: items[].id is the VARIANT id; the line
    // identifier lives in items[].key. CDN scripts (Meta content_ids, Snapchat)
    // read items[].id as the variant id.
    id: extractNumericId(merchandise.id),
    key: line.id || '',
    product_id: extractNumericId(product.id),
    variant_id: extractNumericId(merchandise.id),
    product_title: productTitle,
    variant_title: variantTitle,
    title, // Combined title for FB CDN script
    price: moneyToCents(merchandise.price?.amount),
    quantity: line.quantity || 0,
    sku: merchandise.sku || '',
    vendor: product.vendor || '',
    product_type: productType,
    type: productType, // Alias for FB CDN script
    handle,
    url: handle ? `/products/${handle}` : '',
    image: merchandise.image?.url || '',
    properties: attributes.reduce(
      (acc, attr) => {
        if (attr?.key) {
          acc[attr.key] = attr.value ?? '';
        }
        return acc;
      },
      {} as Record<string, any>
    ),
  };
}

/**
 * Extract cart token from cart ID or checkoutUrl
 * Cart GID format: gid://shopify/Cart/<token>?key=<key>
 * Returns the token only — the same value Shopify's Ajax cart stores in the
 * `cart` cookie.
 */
function getCartToken(cart: StorefrontCart): string {
  const fromId = extractCartToken(cart.id);
  if (fromId) return fromId;

  // Fallback: try to extract from checkoutUrl
  if (cart.checkoutUrl) {
    // checkoutUrl format: https://store.myshopify.com/cart/c/Z2NwLXVzLWN...?key=...
    const urlMatch = cart.checkoutUrl.match(/\/cart\/c\/([^\/\?]+)/);
    if (urlMatch) {
      return urlMatch[1];
    }
  }

  return '';
}

/**
 * Adapt a Storefront API cart to Analyzify format
 * Defensively handles null/undefined cart data
 */
export function adaptCart(cart: StorefrontCart | null | undefined): AnalyzifyCart {
  // Return empty cart for null/undefined
  if (!cart) {
    return createEmptyCart();
  }

  // Safely get cart lines
  const lineEdges = cart.lines?.edges || [];
  const items = lineEdges.map((edge) => adaptCartItem(edge?.node));

  // Extract currency with fallback
  const currency = cart.cost?.totalAmount?.currencyCode || 'USD';

  return {
    items,
    total_price: moneyToCents(cart.cost?.totalAmount?.amount),
    item_count: cart.totalQuantity || 0,
    token: getCartToken(cart),
    currency,
    attributes: (Array.isArray(cart.attributes) ? cart.attributes : []).reduce(
      (acc, attr) => {
        if (attr?.key) {
          acc[attr.key] = attr.value ?? '';
        }
        return acc;
      },
      {} as Record<string, string>
    ),
  };
}

/**
 * Create an empty cart object
 */
export function createEmptyCart(currency: string = 'USD'): AnalyzifyCart {
  return {
    items: [],
    total_price: 0,
    item_count: 0,
    token: '',
    currency,
  };
}
